import { MetastructureField, MetastructureFieldType } from '__types__';
import type { ResourceTypeFieldAssignment } from 'v5/api/resource-types/resource-type-bindings.api';

function metastructureFieldKey(field: MetastructureField): string {
  switch (field.type) {
    case MetastructureFieldType.Core:
      return `core:${field.name}`;
    case MetastructureFieldType.Custom:
      return `custom:${field.custom_field_definition_id}`;
    case MetastructureFieldType.CustomGroup:
      return `group:${field.custom_fieldgroup_definition_id}`;
  }
}

function assignmentKey(assignment: ResourceTypeFieldAssignment): string {
  switch (assignment.source) {
    case 'core':
      return `core:${assignment.coreFieldKey}`;
    case 'custom':
      return `custom:${assignment.customFieldDefinitionId}`;
    case 'group':
      return `group:${assignment.customFieldGroupDefinitionId}`;
  }
}

export function mapAssignmentsToMetastructureFields(
  assignments: ResourceTypeFieldAssignment[],
  previousFields: MetastructureField[] = []
): MetastructureField[] {
  const previousByKey = new Map(
    previousFields.map(field => [metastructureFieldKey(field), field])
  );

  const mapped = assignments.map((assignment): MetastructureField => {
    const shared = {
      active: assignment.active,
      locked: assignment.locked,
      required: assignment.required,
      default: previousByKey.get(assignmentKey(assignment))?.default ?? ''
    };
    switch (assignment.source) {
      case 'core':
        return {
          type: MetastructureFieldType.Core,
          name: assignment.coreFieldKey,
          ...shared
        };
      case 'custom':
        return {
          type: MetastructureFieldType.Custom,
          custom_field_definition_id: assignment.customFieldDefinitionId,
          ...shared
        };
      case 'group':
        return {
          type: MetastructureFieldType.CustomGroup,
          custom_fieldgroup_definition_id:
            assignment.customFieldGroupDefinitionId,
          ...shared
        };
    }
  });

  // Groups the assignments don't name (a workspace overlay never carries
  // them) stay on the metastructure.
  const named = new Set(assignments.map(assignmentKey));
  const keptGroups = previousFields.filter(
    field =>
      field.type === MetastructureFieldType.CustomGroup &&
      !named.has(metastructureFieldKey(field))
  );
  return [...mapped, ...keptGroups];
}
